import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  runTransaction,
  serverTimestamp,
  where,
  limit,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import { getAuth } from "firebase/auth";
const auth = getAuth();

// ✅ Build/version marker (for Cloudflare cache debugging)
const FORM_VERSION = "2026-02-16-v1";
if (typeof window !== "undefined") window.__PARTE_FORM_VERSION__ = FORM_VERSION;


/** ---------------------------
 * Helpers
 * -------------------------- */
function companiaKey(compania) {
  return String(compania || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w]/g, "");
}


function bomberoLabel(b) {
  const nombre = b?.nombre || "";
  const cargo = b?.cargo ? ` (${b.cargo})` : "";
  return `${nombre}${cargo}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function getYearFromFechaEmergencia(fechaEmergencia) {
  const y = String(fechaEmergencia || "").slice(0, 4);
  const n = Number(y);
  if (Number.isFinite(n) && n > 2000) return n;
  return new Date().getFullYear();
}

function splitHora(hhmm) {
  const s = String(hhmm || "");
  const m = s.match(/^(\d{2}):(\d{2})$/);
  if (!m) return { hh: "", mm: "" };
  return { hh: m[1], mm: m[2] };
}

/**
 * ✅ Selector 24h garantizado (Safari-proof)
 * value: "HH:MM" o ""
 */
function Time24Select({ value, onChange, disabled }) {
  const { hh, mm } = useMemo(() => splitHora(value), [value]);

  const horas = useMemo(() => Array.from({ length: 24 }, (_, i) => pad2(i)), []);
  const minutos = useMemo(
    () => Array.from({ length: 60 }, (_, i) => pad2(i)),
    []
  );

  function setHH(newHH) {
    if (!newHH) {
      onChange("");
      return;
    }
    const newMM = mm || "00";
    onChange(`${newHH}:${newMM}`);
  }

  function setMM(newMM) {
    if (!newMM) {
      onChange("");
      return;
    }
    const newHH = hh || "00";
    onChange(`${newHH}:${newMM}`);
  }

  return (
    <div style={S.timeWrap}>
      <select
        value={hh}
        onChange={(e) => setHH(e.target.value)}
        style={S.timeSelect}
        disabled={disabled}
      >
        <option value="">HH</option>
        {horas.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>

      <div style={S.timeColon}>:</div>

      <select
        value={mm}
        onChange={(e) => setMM(e.target.value)}
        style={S.timeSelect}
        disabled={disabled}
      >
        <option value="">MM</option>
        {minutos.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}

/** ---------------------------
 * 🔥 RUTA REAL (según tu Firestore)
 * tipos:  /tiposEmergencia/items/items/{tipoId}
 * items:  /tiposEmergencia/items/items/{tipoId}/items/{itemId}
 * -------------------------- */
function tiposEmergenciaCollectionRef() {
  return collection(db, "tiposEmergencia", "items", "items");
}

function itemsDeTipoCollectionRef(tipoId) {
  return collection(db, "tiposEmergencia", "items", "items", tipoId, "items");
}

/** ---------------------------
 * ✅ Helpers RUT (simple)
 * -------------------------- */
function limpiarRut(raw) {
  return String(raw || "").replace(/[^0-9kK]/g, "").toUpperCase();
}
function formatearRut(raw) {
  const r = limpiarRut(raw);
  if (r.length < 2) return r;
  const cuerpo = r.slice(0, -1);
  const dv = r.slice(-1);
  return `${cuerpo}-${dv}`;
}

/** ---------------------------
 * ✅ Formulario dependiente por CLAVE PRINCIPAL (ID del doc)
 * -------------------------- */

// ⚠️ AJUSTA AQUÍ si tus docId son distintos
const DETALLE_TIPO_IDS = {
  ESTRUCTURAL: "llamado_estructural", // 10-0
  PASTIZALES: "llamado_a_pastizales_o_basura", // 10-2
  RESCATE_VEHICULAR: "llamado_a_rescate_vehicular", // 10-4
};

function emptyEdificacion() {
  return {
    tipoEdificacion: "",
    numero: "", // N° casa o dpto
    pisos: "",
    estructuraAfectada: "",
    propietarioNombre: "",
    propietarioRut: "",
    propietarioTelefono: "",
  };
}

function emptyVehiculo() {
  return {
    tipoVehiculo: "",
    marca: "",
    modelo: "",
    patente: "",
    tipoCombustible: "",
    propietarioNombre: "",
    propietarioRut: "",
    propietarioTelefono: "",
  };
}

function templateByTipoId(tipoId) {
  switch (tipoId) {
    case DETALLE_TIPO_IDS.ESTRUCTURAL:
      return { edificaciones: [emptyEdificacion()] };

    case DETALLE_TIPO_IDS.PASTIZALES:
      return {
        metrosCuadradosAfectados: "",
        arbolesNativos: "", // "SI" | "NO"
        propietarioNombre: "",
        propietarioRut: "",
        propietarioTelefono: "",
      };

    case DETALLE_TIPO_IDS.RESCATE_VEHICULAR:
      return {
        vehiculos: [emptyVehiculo()],
        extricacion: "", // "SI" | "NO"
      };

    default:
      return null;
  }
}


// Normaliza texto para búsqueda (sin tildes, sin mayúsculas)
function normText(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Selector con buscador para bomberos (insensible a tildes/mayúsculas).
 * - Escribe para filtrar
 * - Click en resultado para seleccionar
 * - Botón "Limpiar" para quitar selección
 */
function BomberoSearchSelect({
  label,
  valueId,
  onChange,
  query,
  setQuery,
  bomberos,
  placeholder = "Buscar por nombre o RUT…",
  disabled = false,
  hint = "",
  required = false,
  styles = {},
}) {
  const selected = useMemo(
    () => (valueId ? bomberos.find((b) => b?.id === valueId) : null),
    [bomberos, valueId]
  );

  // Si viene seleccionado y el input está vacío, mostramos el label
  useEffect(() => {
    if (selected && !query) setQuery(bomberoLabel(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const q = normText(query);
  const filtered = useMemo(() => {
    if (!q) return [];
    const out = [];
    for (const b of bomberos) {
      const nombre = normText(b?.nombre);
      const rut = normText(b?.rut || b?.id);
      const cargo = normText(b?.cargo);
      if (
        nombre.includes(q) ||
        rut.includes(q) ||
        cargo.includes(q)
      ) {
        out.push(b);
      }
      if (out.length >= 12) break;
    }
    return out;
  }, [bomberos, q]);

  const showDropdown = Boolean(q) && filtered.length > 0;

  return (
    <div style={{ width: "100%", ...styles.wrap }}>
      <label style={styles.label}>
        {label}
      </label>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Si el usuario empieza a buscar algo distinto, limpiamos selección
            if (valueId) onChange("");
          }}
          placeholder={placeholder}
          disabled={disabled}
          style={styles.input}
        />
        <button
          type="button"
          onClick={() => {
            onChange("");
            setQuery("");
          }}
          disabled={disabled && !valueId}
          style={styles.smallBtn}
        >
          Limpiar
        </button>
      </div>

      {hint ? <div style={styles.hint}>{hint}</div> : null}

      {showDropdown && (
        <div style={styles.dropdown}>
          {filtered.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                onChange(b.id);
                setQuery(bomberoLabel(b));
              }}
              style={styles.dropdownItem}
            >
              {bomberoLabel(b)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ParteFormulario() {
  const navigate = useNavigate();
  const { cuerpoSlug } = useCuerpo();
  const partesBase = `/${cuerpoSlug}/partes`;
  const auth = getAuth();

// ✅ Build/version marker (for Cloudflare cache debugging)
const FORM_VERSION = "2026-02-16-v1";
if (typeof window !== "undefined") window.__PARTE_FORM_VERSION__ = FORM_VERSION;

  const [params] = useSearchParams();
  const compania = params.get("compania") || "";
  const parteId = params.get("id") || "";
  const isEdit = !!parteId;


  // ✅ Perfil de PARTES (para permisos y IDs estables)
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return;
      try {
        const snap = await getDoc(doc(db, "usuarios_partes", u.uid));
        if (!snap.exists()) return;
        const p = snap.data() || {};
        if (typeof p.cuerpo_id === "string") setCuerpoId(p.cuerpo_id);
        // usamos compania_id del perfil como docId estable para reglas
        if (typeof p.compania_id === "string") setCompaniaDocId(p.compania_id);
        if (typeof p.compania === "string") setCompaniaNombrePerfil(p.compania);
        if (typeof p.perfil === "string") setPerfilPartes(p.perfil);
        if (typeof p.rol === "string") setRolPartes(p.rol);
      } catch (e) {
        console.warn("⚠️ No se pudo cargar usuarios_partes:", e);
      }
    });
    return () => unsub();
  }, []);


  // ✅ responsive
  const [isSmall, setIsSmall] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < 980
  );
  useEffect(() => {
    function onResize() {
      setIsSmall(window.innerWidth < 980);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lists
  const [bomberosCompania, setBomberosCompania] = useState([]);
  const [bomberosTodos, setBomberosTodos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [subTipos, setSubTipos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [companiasById, setCompaniasById] = useState({}); // id -> nombre
  const [jurisdicciones, setJurisdicciones] = useState([]);
  const [operadoras, setOperadoras] = useState([]);
  const [operadoraId, setOperadoraId] = useState("");
  const [zona, setZona] = useState(""); // "urbano" | "rural" | "costa"
  const [companiasDespachadasIds, setCompaniasDespachadasIds] = useState([]);
  const [companiasDespachadasNombres, setCompaniasDespachadasNombres] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ✅ contexto (según Firestore real)
  const [cuerpoId, setCuerpoId] = useState("");
  const [companiaDocId, setCompaniaDocId] = useState("");
  const [companiaNombrePerfil, setCompaniaNombrePerfil] = useState("");

  const [perfilPartes, setPerfilPartes] = useState("");
  const [rolPartes, setRolPartes] = useState("");

  // Form fields
  // 🔢 Correlativo anual (se genera automáticamente al guardar)
  const [correlativoPreview, setCorrelativoPreview] = useState("");
  const [enCreacionDocId, setEnCreacionDocId] = useState(""); // partes_en_creacion/{correlativo}
  const [correlativoNumReservado, setCorrelativoNumReservado] = useState(null);
  const [correlativoYearReservado, setCorrelativoYearReservado] = useState(null);

  // 📅 Fecha/Hora del despacho (obligatorio)
  const [fechaEmergencia, setFechaEmergencia] = useState("");
  const [horaDespacho, setHoraDespacho] = useState("");
  const [tipoEmergenciaId, setTipoEmergenciaId] = useState("");
  const [subTipoEmergenciaId, setSubTipoEmergenciaId] = useState("");
  const [direccion, setDireccion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [jurisdiccion, setJurisdiccion] = useState("");
  const [realizaParteId, setRealizaParteId] = useState("");
  const [realizaParteQuery, setRealizaParteQuery] = useState("");
  const [obacCuerpoId, setObacCuerpoId] = useState("");
  const [obacCuerpoQuery, setObacCuerpoQuery] = useState("");
  const skipDetalleTemplateOnceRef = useRef(false);
  const [asistentesIds, setAsistentesIds] = useState([]);

  // =========================
  // ✅ MODO EDICIÓN (si viene ?id=parteId)
  // =========================
  useEffect(() => {
    let cancelled = false;

    async function loadParteForEdit() {
      if (!isEdit) return;
      if (!parteId) return;

      try {
        setLoading(true);
        const ref = doc(db, "partes_emergencia", parteId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          alert("❌ No se encontró el parte para editar.");
          navigate(partesBase);
          return;
        }

        const data = snap.data() || {};

        // Pre-cargar campos principales
        setCorrelativoPreview(data.correlativo || data.folio || "");
        setFechaEmergencia(data.fechaEmergencia || "");
        setHoraDespacho(data.horaDespacho || "");
        skipDetalleTemplateOnceRef.current = true;
        setTipoEmergenciaId(data.tipoEmergenciaId || data.tipo_emergencia_id || "");
        setSubTipoEmergenciaId(data.subTipoEmergenciaId || data.sub_tipo_emergencia_id || "");
        setDireccion(data.direccion || "");
        setObservaciones(data.observaciones || "");
        setJurisdiccion(data.jurisdiccion || "");

        // Zona (urbano/rural/costa)
        setZona(data.zona || data.zonaServicio || "");

        // Operadora / quién realiza el despacho
        const op = data.operadora || null;
        if (op && typeof op === "object" && (op.id || op.rut)) {
          setOperadoraId(op.id || op.rut || "");
        } else {
          setOperadoraId(
            data.operadoraId ||
              data.operadora_id ||
              data.realizaParteId ||
              data.realiza_parte_id ||
              ""
          );
        }

        // Compañías despachadas
        const ids = data.companias_despachadas_ids || data.companiasDespachadasIds || [];
        const nms = data.companias_despachadas_nombres || data.companiasDespachadasNombres || [];
        setCompaniasDespachadasIds(Array.isArray(ids) ? ids : []);
        setCompaniasDespachadasNombres(Array.isArray(nms) ? nms : []);

        // Asistentes
        const asis = data.asistentesIds || [];
        setAsistentesIds(Array.isArray(asis) ? asis : []);

        // Unidades (reconstrucción retrocompatible desde Firebase)
        const ups = data.unidadesParte || [];
        if (Array.isArray(ups) && ups.length) {
          const reconstruidas = ups.map((u) => ({
            unidadId:
              u?.unidadId ||
              u?.obacCompania?.unidadId ||
              u?.unidadSnapshot?.id ||
              u?.unidad?.id ||
              "",
            horaSalida: u?.horaSalida || "",
            horaLlegada: u?.horaLlegada || "",
            kmSalida: u?.kmSalida || "",
            kmLlegada: u?.kmLlegada || "",
            conductorId:
              u?.conductorId ||
              u?.conductor?.id ||
              u?.conductorSnapshot?.id ||
              "",
            obacCompaniaId:
              u?.obacCompaniaId ||
              u?.obacCompania?.id ||
              u?.obacSnapshot?.id ||
              "",
          }));
          setUnidadesParte(reconstruidas);
        } else {
          setUnidadesParte([]);
        }

        // Detalle específico (cargar exactamente lo que exista en Firebase)
        const det = data.detalleEspecifico || null;
        if (det && typeof det === "object") {
          if (det.tipo && det.data !== undefined) {
            setDetalleEspecifico(det.data || null);
          } else {
            setDetalleEspecifico(det);
          }
        } else {
          setDetalleEspecifico(null);
        }

        // Realiza parte (si existe)
        const rp = data.realizaParte || null;
        if (rp && typeof rp === "object" && rp.id) {
          setRealizaParteId(rp.id);
          setRealizaParteQuery(rp.nombre || "");
        } else {
          setRealizaParteId(data.realizaParteId || data.realiza_parte_id || "");
        }

        // OBAC cuerpo (si existe en tu estructura)
        const ob = data.obacCuerpo || data.obac_cuerpo || null;
        if (ob && typeof ob === "object" && ob.id) {
          setObacCuerpoId(ob.id);
          setObacCuerpoQuery(ob.nombre || "");
        } else {
          setObacCuerpoId(data.obacCuerpoId || data.obac_cuerpo_id || "");
        }

        // En edición NO usamos partes_en_creacion
        setEnCreacionDocId("");
        setCorrelativoNumReservado(null);
        setCorrelativoYearReservado(null);
      } catch (e) {
        console.error("❌ Error cargando parte para editar:", e);
        alert("❌ No se pudo cargar el parte para editar.");
        navigate(partesBase);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadParteForEdit();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, parteId]);

  // ✅ NUEVO: detalle dependiente de la clave principal (docId)
  const [detalleEspecifico, setDetalleEspecifico] = useState(null);

  const [unidadesParte, setUnidadesParte] = useState([
    {
      unidadId: "",
      horaSalida: "",
      horaLlegada: "",
      kmSalida: "",
      kmLlegada: "",
      obacCompaniaId: "",
      conductorId: "",
    },
  ]);

  useEffect(() => {
    let mounted = true;

    async function cargar() {
      setLoading(true);

      // ✅ Modo GLOBAL: si no viene ?compania, cargamos todo (todas las compañías)

      try {
        let _companiaDocId = "";
        let _cuerpoId = "";

        // 0) Resolver SIEMPRE el Cuerpo desde el perfil antes de consultar catálogos.
        try {
          const u = auth.currentUser;
          if (u && u.uid) {
            const perfilSnap = await getDoc(doc(db, "usuarios_partes", u.uid));
            if (perfilSnap.exists()) {
              const perfil = perfilSnap.data() || {};
              _cuerpoId = String(perfil?.cuerpo_id || "").trim();
              if (!_companiaDocId && typeof perfil?.compania_id === "string") {
                _companiaDocId = perfil.compania_id;
              }
            }
          }
        } catch (e) {
          console.warn("⚠️ No se pudo resolver cuerpo_id desde usuarios_partes:", e);
        }

        // Si viene ?compania, resolverla SOLO dentro del Cuerpo actual.
        if (compania && _cuerpoId) {
          try {
            const snapCia = await getDocs(
              query(collection(db, "companias"), where("cuerpo_id", "==", _cuerpoId))
            );
            const d = snapCia.docs.find(
              (x) => String(x.data()?.nombre || "").trim() === String(compania).trim()
            );
            if (d) _companiaDocId = d.id;
          } catch (e) {
            console.warn("⚠️ No se pudo resolver la compañía dentro del Cuerpo:", e);
          }
        }

        if (mounted) {
          setCompaniaDocId(_companiaDocId);
          setCuerpoId(_cuerpoId);
        }

        // 1) ✅ Bomberos (GLOBAL + por compañía si aplica)
        let listCia = [];
        let listAll = [];
        if (_cuerpoId) {
          const snapAll = await getDocs(
            query(collection(db, "bomberos"), where("cuerpo_id", "==", _cuerpoId))
          );
          listAll = snapAll.docs.map((d) => ({ id: d.id, ...d.data() }));
          if (compania) {
            listCia = listAll.filter(
              (b) => String(b?.compania || "").trim() === String(compania).trim()
            );
          }
        }

        if (!mounted) return;

        listCia.sort((a, b) =>
          String(a?.nombre || "").localeCompare(String(b?.nombre || ""))
        );
        listAll.sort((a, b) =>
          String(a?.nombre || "").localeCompare(String(b?.nombre || ""))
        );

        setBomberosCompania(listCia);
        setBomberosTodos(listAll);
        // ✅ En global elegimos el primero de todos; en modo compañía, el primero de la compañía.
        if (!isEdit) setRealizaParteId((compania ? listCia[0]?.id : listAll[0]?.id) || "");

        // 3) ✅ Tipos emergencia
        try {
          const snapTipos = await getDocs(tiposEmergenciaCollectionRef());
          const listTipos = snapTipos.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => x?.activo !== false);

          listTipos.sort(
            (a, b) =>
              Number(a?.orden ?? a?.order ?? 0) - Number(b?.orden ?? b?.order ?? 0)
          );

          if (mounted) {
            setTipos(listTipos);
            const firstId = listTipos[0]?.id || "";
            if (!isEdit && !tipoEmergenciaId) setTipoEmergenciaId(firstId);
          }
        } catch (e) {
          console.warn("⚠️ No se pudo cargar tiposEmergencia:", e);
          if (mounted) {
            setTipos([]);
            setTipoEmergenciaId("");
            setSubTipos([]);
            setSubTipoEmergenciaId("");
          }
        }

        // 4) ✅ Unidades
        try {
          const baseRef = collection(db, "unidades");
          const qUn = _cuerpoId
            ? query(baseRef, where("cuerpo_id", "==", _cuerpoId))
            : query(baseRef, where("cuerpo_id", "==", "__SIN_CUERPO__"));

          const snapUn = await getDocs(qUn);
          let listUn = snapUn.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => (x?.activo ?? x?.operativa ?? true) !== false);

          if (compania) {
            if (_companiaDocId) {
              listUn = listUn.filter((x) => x?.compania_id === _companiaDocId);
            } else {
              listUn = listUn.filter(
                (x) => String(x?.compania || "").trim() === String(compania).trim()
              );
            }
          }

          listUn.sort(
            (a, b) =>
              Number(a?.orden ?? a?.order ?? 0) - Number(b?.orden ?? b?.order ?? 0)
          );

          if (mounted) setUnidades(listUn);
        } catch (e) {
          console.warn("⚠️ No se pudo cargar unidades:", e);
          if (mounted) setUnidades([]);
        }

        // 5) ✅ Compañías (para resolver compania_id en unidades)
        try {
          const snapC = _cuerpoId
            ? await getDocs(query(collection(db, "companias"), where("cuerpo_id", "==", _cuerpoId)))
            : { docs: [] };
          const map = {};
          snapC.docs.forEach((d) => {
            const data = d.data() || {};
            const nombre = String(data?.nombre || "").trim();
            if (nombre) map[d.id] = nombre;
          });
          if (mounted) setCompaniasById(map);
        } catch (e) {
          console.warn("⚠️ No se pudo cargar companias:", e);
          if (mounted) setCompaniasById({});
        }

        // 6) ✅ Jurisdicciones
        try {
          const baseRef = collection(db, "jurisdicciones");
          const qJ = _cuerpoId
            ? query(baseRef, where("cuerpo_id", "==", _cuerpoId))
            : query(baseRef);

          const snapJ = await getDocs(qJ);
          const listJ = snapJ.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => x?.activo !== false);

          listJ.sort(
            (a, b) =>
              Number(a?.orden ?? a?.order ?? 0) - Number(b?.orden ?? b?.order ?? 0)
          );

          const nombres = listJ.map((j) => j?.nombre).filter(Boolean);

          if (mounted) {
            setJurisdicciones(nombres);
            if (!isEdit) setJurisdiccion(nombres[0] || "");
          }
        } catch (e) {
          console.warn("⚠️ No se pudo cargar jurisdicciones:", e);
          if (mounted) {
            setJurisdicciones([]);
            setJurisdiccion("");
          }
        }

        // 7) ✅ Operadoras (quien realiza el despacho)
        try {
          const snapO = _cuerpoId
            ? await getDocs(query(collection(db, "operadoras"), where("cuerpo_id", "==", _cuerpoId)))
            : { docs: [] };
          const listO = snapO.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((x) => x?.activo !== false);

          listO.sort((a, b) =>
            String(a?.nombreCompleto || a?.nombre || "").localeCompare(
              String(b?.nombreCompleto || b?.nombre || "")
            )
          );

          if (mounted) {
            setOperadoras(listO);
            // En modo crear, si aún no hay operadora seleccionada, ponemos la primera
            if (!isEdit && !operadoraId) setOperadoraId(listO[0]?.id || "");
          }
        } catch (e) {
          console.warn("⚠️ No se pudo cargar operadoras:", e);
          if (mounted) setOperadoras([]);
        }

        // 🔢 Previsualizar correlativo anual (solo UI)
        try {
          // ✅ En edición NUNCA recalcular preview (evita que el 00016 se pise con 00001)
          if (!isEdit) {
            const year =
              getYearFromFechaEmergencia(fechaEmergencia) || new Date().getFullYear();

            // ✅ Usar el cuerpo LOCAL resuelto arriba (NO state)
            // Si estás en modo global y _cuerpoId viene vacío, recién usar "global"
            const cuerpoKey = String(_cuerpoId || "global").toLowerCase().trim();
            const key = `parte_${cuerpoKey}_${year}`;

            const folioSnap = await getDoc(doc(db, "folios_partes", key));
            const data = folioSnap.exists() ? folioSnap.data() : null;

            const raw =
              typeof data?.nextFolio === "number"
                ? data.nextFolio
                : typeof data?.ultimoFolio === "number"
                ? data.ultimoFolio + 1
                : 1;

            const next = Number.isFinite(Number(raw)) ? Number(raw) : 1;
            const prev = `${String(next).padStart(5, "0")}-${year}`;

            if (mounted) setCorrelativoPreview(prev);
          }
        } catch (e) {
          if (mounted) setCorrelativoPreview("");
        }
      } catch (e) {
        console.error("❌ Error cargando formulario:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    cargar();
    return () => {
      mounted = false;
    };
  }, [compania]);

  // ✅ Cuando cambia el tipo principal, cargar su subcolección items
  useEffect(() => {
    let mounted = true;

    async function cargarSubTipos() {
      if (!tipoEmergenciaId) {
        setSubTipos([]);
        setSubTipoEmergenciaId("");
        return;
      }
      try {
        const snap = await getDocs(itemsDeTipoCollectionRef(tipoEmergenciaId));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => x?.activo !== false);

        list.sort((a, b) => {
          const ao = Number(a?.orden ?? a?.order ?? NaN);
          const bo = Number(b?.orden ?? b?.order ?? NaN);
          if (!Number.isNaN(ao) && !Number.isNaN(bo)) return ao - bo;
          return String(a?.nombre || a?.id || "").localeCompare(
            String(b?.nombre || b?.id || "")
          );
        });

        if (!mounted) return;

        setSubTipos(list);
        if (!isEdit && !subTipoEmergenciaId) setSubTipoEmergenciaId(list[0]?.id || "");
      } catch (e) {
        console.warn("⚠️ No se pudo cargar sub-items del tipo:", tipoEmergenciaId, e);
        if (!mounted) return;
        setSubTipos([]);
        setSubTipoEmergenciaId("");
      }
    }

    cargarSubTipos();
    return () => {
      mounted = false;
    };
  }, [tipoEmergenciaId]);

  const tipoSeleccionado = useMemo(
    () => tipos.find((t) => t.id === tipoEmergenciaId),
    [tipos, tipoEmergenciaId]
  );

  const subTipoSeleccionado = useMemo(
    () => subTipos.find((x) => x.id === subTipoEmergenciaId),
    [subTipos, subTipoEmergenciaId]
  );

  // ✅ NUEVO: cuando cambia la clave principal (ID), inicializa el detalle dependiente
  useEffect(() => {
    if (skipDetalleTemplateOnceRef.current) {
      skipDetalleTemplateOnceRef.current = false;
      return;
    }
    const tpl = templateByTipoId(tipoEmergenciaId);
    setDetalleEspecifico(tpl);
  }, [tipoEmergenciaId]);

  // ✅ Lista de maquinistas SOLO de la compañía (para Conductor)
  const maquinistasCompania = useMemo(() => {
    return bomberosCompania.filter((b) => b?.maquinista === true);
  }, [bomberosCompania]);


  // ✅ GLOBAL: maquinistas de todas las compañías
  const maquinistasTodos = useMemo(() => {
    return bomberosTodos.filter((b) => b?.maquinista === true);
  }, [bomberosTodos]);

  // ✅ Mapas para agrupar bomberos por compañía (para selects globales)
  const bomberosPorCompania = useMemo(() => {
    const map = {};
    for (const b of bomberosTodos) {
      const cia = b?.compania || "Sin compañía";
      if (!map[cia]) map[cia] = [];
      map[cia].push(b);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) =>
        String(a?.nombre || "").localeCompare(String(b?.nombre || ""))
      );
    }
    return map;
  }, [bomberosTodos]);

  // ✅ OBAC por fila: depende de la unidad seleccionada (resuelve compania_id -> nombre)
  function obacCompaniaOptionsByRow(idx) {
    const row = unidadesParte[idx];
    const u = unidades.find((x) => x.id === row?.unidadId);

    const ciaFromId =
      u?.compania_id && companiasById ? companiasById[u.compania_id] : "";
    const cia =
      ciaFromId ||
      u?.compania ||
      u?.compania_nombre ||
      u?.companiaNombre ||
      u?.companiaName ||
      "";

    if (!cia) return [];
    return (bomberosPorCompania[cia] || []).slice();
  }

  /** ---------------------------
   * ✅ Render + handlers para detalle específico (por ID)
   * -------------------------- */
  const detalleAplica = useMemo(() => {
    return Object.values(DETALLE_TIPO_IDS).includes(tipoEmergenciaId);
  }, [tipoEmergenciaId]);

  function setDetalleField(field, value) {
    setDetalleEspecifico((prev) => ({ ...(prev || {}), [field]: value }));
  }

  function setDetalleEdifField(idx, field, value) {
    setDetalleEspecifico((prev) => {
      const edificaciones = Array.isArray(prev?.edificaciones) ? prev.edificaciones : [];
      const next = edificaciones.map((e, i) => (i === idx ? { ...e, [field]: value } : e));
      return { ...(prev || {}), edificaciones: next };
    });
  }

  function addEdificacion() {
    setDetalleEspecifico((prev) => {
      const edificaciones = Array.isArray(prev?.edificaciones) ? prev.edificaciones : [];
      return { ...(prev || {}), edificaciones: [...edificaciones, emptyEdificacion()] };
    });
  }

  function removeEdificacion(idx) {
    setDetalleEspecifico((prev) => {
      const edificaciones = Array.isArray(prev?.edificaciones) ? prev.edificaciones : [];
      const next = edificaciones.filter((_, i) => i !== idx);
      return { ...(prev || {}), edificaciones: next.length ? next : [emptyEdificacion()] };
    });
  }

  function setDetalleVehField(idx, field, value) {
    setDetalleEspecifico((prev) => {
      const vehiculos = Array.isArray(prev?.vehiculos) ? prev.vehiculos : [];
      const next = vehiculos.map((v, i) => (i === idx ? { ...v, [field]: value } : v));
      return { ...(prev || {}), vehiculos: next };
    });
  }

  function addVehiculo() {
    setDetalleEspecifico((prev) => {
      const vehiculos = Array.isArray(prev?.vehiculos) ? prev.vehiculos : [];
      return { ...(prev || {}), vehiculos: [...vehiculos, emptyVehiculo()] };
    });
  }

  function removeVehiculo(idx) {
    setDetalleEspecifico((prev) => {
      const vehiculos = Array.isArray(prev?.vehiculos) ? prev.vehiculos : [];
      const next = vehiculos.filter((_, i) => i !== idx);
      return { ...(prev || {}), vehiculos: next.length ? next : [emptyVehiculo()] };
    });
  }
  function validateDetalleEspecifico() {
    // 🔓 Sin campos obligatorios por ahora (según solicitud).
    // Más adelante podemos reactivar requeridos por sección/tipo.
    return { ok: true, msg: "" };
  }

  const valida = useMemo(() => {
    // 🔓 Sin validaciones obligatorias por ahora (según solicitud).
    return { ok: true, msg: "" };
  }, []);

  function toggleCompaniaDespachada(comp) {
    const id = comp?.id || comp?.nombre || "";
    const nombre = comp?.nombre || comp?.id || "";
    if (!id) return;
    setCompaniasDespachadasIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
    setCompaniasDespachadasNombres((prev) => {
      if (prev.includes(nombre)) return prev.filter((x) => x !== nombre);
      return [...prev, nombre];
    });
  }

  function toggleAsistente(id) {
    setAsistentesIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  }

  function updateUnidadRow(idx, patch) {
    setUnidadesParte((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row))
    );
  }

  function addUnidadRow() {
    setUnidadesParte((prev) => [
      ...prev,
      {
        unidadId: "",
        horaSalida: "",
        horaLlegada: "",
        kmSalida: "",
        kmLlegada: "",
        obacCompaniaId: "",
          conductorId: "",
      },
    ]);
  }

  function removeUnidadRow(idx) {
    setUnidadesParte((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  }

  
  async function reservarParteEnCreacion({ tipoEmergenciaId, tipoEmergenciaNombre, year }) {
    // ✅ Marca "parte en creación" SIN consumir folio.
    // El folio se incrementa SOLO al guardar el parte final (transacción atómica).
    const y = Number(year) || Number(getYearFromFechaEmergencia(fechaEmergencia));
    if (!y || !Number.isFinite(y)) {
      throw new Error("Año inválido para marcar parte en creación (fechaEmergencia vacía o incorrecta)."
      );
    }

    const user = auth.currentUser;
    const uid = user?.uid || "";
    const email = user?.email || "";

    const now = new Date();
    const expiresAt = Timestamp.fromDate(new Date(now.getTime() + 20 * 60 * 1000));

    // DocId estable por usuario + año (evita duplicidad por el mismo usuario)
    const docId = `${uid}_${y}`;
    const enCreacionRef = doc(db, "partes_en_creacion", docId);

    await runTransaction(db, async (tx) => {
      tx.set(
        enCreacionRef,
        {
          anio: y,
          cuerpo_id: cuerpoId || "",
          compania: compania || "",
          // ✅ importante: guarda compania_id si está disponible en tu estado/perfil
          compania_id: (companiaDocId || null),
          tipo_emergencia_id: tipoEmergenciaId || "",
          tipo_emergencia_nombre: tipoEmergenciaNombre || "",
          creado_por_uid: uid,
          creado_por_email: email,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          expires_at: expiresAt,
        },
        { merge: true }
      );
    });

    return { enCreacionDocId: docId, year: y };
  }


  async function actualizarParteEnCreacion(patch, year) {
    if (!enCreacionDocId) return;

    const ref = doc(db, "partes_en_creacion", enCreacionDocId);

    const now = new Date();
    const expiresAt = Timestamp.fromDate(new Date(now.getTime() + 20 * 60 * 1000));

    // ✅ Si viene year, lo guardamos como referencia (pero NO cambia docId)
    const y = Number(year);
    const extra = Number.isFinite(y) && y > 2000 ? { anio: y } : {};

    try {
      await runTransaction(db, async (tx) => {
        tx.set(
          ref,
          {
            ...patch,
            ...extra,
            updated_at: serverTimestamp(),
            expires_at: expiresAt,
          },
          { merge: true }
        );
      });
    } catch (e) {
      console.warn("⚠️ No se pudo actualizar partes_en_creacion:", e);
    }
  }


  // ✅ Reserva automática cuando hay tipo seleccionado (anti-duplicidad entre usuarios)
  useEffect(() => {
    if (isEdit) return; // ✅ en edición NO reservamos 'partes_en_creacion'
    let cancelled = false;

    async function run() {
      if (!tipoEmergenciaId) return;

      // ✅ Año del correlativo se basa en la fecha de emergencia (no en payload)
      const year = getYearFromFechaEmergencia(fechaEmergencia);

      // Si no hay año válido (fecha vacía o mala), no intentamos reservar
      if (!year) return;

      const tipoNombre = tipoSeleccionado?.nombre || "";

      // ✅ Caso 1: Ya existe reserva
      // - Si el año NO cambió, solo renueva expiración y actualiza tipo (no re-reservar)
      // - Si el año cambió, hay que reservar otro correlativo para el nuevo año
      if (enCreacionDocId && correlativoPreview) {
        const mismoAnio = Number(correlativoYearReservado) === Number(year);

        if (mismoAnio) {
          await actualizarParteEnCreacion({
            tipo_emergencia_id: tipoEmergenciaId,
            tipo_emergencia_nombre: tipoNombre,
          });
          return;
        }

        // Año cambió: liberar reserva anterior y reservar nueva
        try {
          // si tienes función para eliminar la reserva anterior, úsala:
          // await eliminarParteEnCreacion(enCreacionDocId);

          // Si NO la tienes, igual reservamos la nueva; la anterior expirará sola por expires_at
          // (lo ideal es eliminarla, pero no lo forzamos para no romper tu estructura actual)
        } catch (e) {
          console.warn("⚠️ No se pudo liberar reserva anterior, continuará por expiración:", e);
        }
      }

      // ✅ Caso 2: No hay marca o el año cambió → marcar "en creación" (sin consumir folio)
      try {
        const res = await reservarParteEnCreacion({
          tipoEmergenciaId,
          tipoEmergenciaNombre: tipoNombre,
          year, // ✅ importante: reservar según año de fechaEmergencia
        });

        if (cancelled) return;

        setCorrelativoPreview("EN CREACIÓN");
        setEnCreacionDocId(res.enCreacionDocId);
        setCorrelativoNumReservado(null);
        setCorrelativoYearReservado(res.year);
      } catch (e) {
        console.error("❌ Error reservando correlativo en creación:", e);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [
    tipoEmergenciaId,
    tipoSeleccionado,
    fechaEmergencia,
    enCreacionDocId,
    correlativoPreview,
    correlativoYearReservado,
    isEdit,
  ]);


async function obtenerFolioYCrearParte(payload, opts = {}) {
    // ✅ Correlativo anual: 0001-YYYY (reinicia cada año)
    const year = getYearFromFechaEmergencia(fechaEmergencia);
    const key = `parte_${(cuerpoId || "global").toLowerCase()}_${year}`;
    const folioRef = doc(db, "folios_partes", key);
    const parteRef = doc(collection(db, "partes_emergencia"));
    const enCreacionId = opts?.enCreacionDocId || "";
    const enCreacionRef = enCreacionId ? doc(db, "partes_en_creacion", enCreacionId) : null;

    return runTransaction(db, async (tx) => {
      const snap = await tx.get(folioRef);

      let next = 1;
      if (snap.exists()) {
        const data = snap.data();
        next = typeof data?.nextFolio === "number" ? data.nextFolio : 1;
      }

      tx.set(folioRef, { nextFolio: next + 1, cuerpo_id: cuerpoId }, { merge: true });
      const correlativoNum = next;
      const correlativoText = `${String(correlativoNum).padStart(5, "0")}-${year}`;

      tx.set(parteRef, {
        ...payload,
        correlativo_num: correlativoNum,
        correlativo_year: year,
        correlativo: correlativoText,
      });

      // ✅ Si existe marca "en creación", la eliminamos SOLO si el parte se creó OK (misma transacción)
      if (enCreacionRef) {
        tx.delete(enCreacionRef);
      }

      return { correlativo: correlativoText, folio: correlativoText, parteId: parteRef.id };
    });
  }

  async function guardar() {

    // ✅ Asegurar que tenemos companiaDocId/cuerpoId (evita permiso-denied por payload incompleto)
    let _companiaDocId = companiaDocId;
    let _cuerpoId = cuerpoId;
    let _companiaNombre = companiaNombrePerfil;
    // ✅ y además (clave para rules) traer perfil/rol desde usuarios_partes
    let _perfilPartes = "";
    let _rolPartes = "";

    // En edición SIEMPRE leemos usuarios_partes para detectar perfil/rol aunque ya tengamos IDs.
    if ((isEdit || !_companiaDocId || !_cuerpoId || !_companiaNombre) && auth.currentUser) {
      try {
        const snapP = await getDoc(doc(db, "usuarios_partes", auth.currentUser.uid));
        if (snapP.exists()) {
          const p = snapP.data() || {};
          if (!_cuerpoId && typeof p.cuerpo_id === "string") _cuerpoId = p.cuerpo_id;
          if (!_companiaDocId && typeof p.compania_id === "string") _companiaDocId = p.compania_id;
          if (!_companiaNombre && typeof p.compania === "string") _companiaNombre = p.compania;
          if (typeof p.perfil === "string") _perfilPartes = p.perfil;
          if (typeof p.rol === "string") _rolPartes = p.rol;
          // sincroniza estado (no bloqueante)
          if (_cuerpoId && _cuerpoId !== cuerpoId) setCuerpoId(_cuerpoId);
          if (_companiaDocId && _companiaDocId !== companiaDocId) setCompaniaDocId(_companiaDocId);
          if (_companiaNombre && _companiaNombre !== companiaNombrePerfil) setCompaniaNombrePerfil(_companiaNombre);
          if (_perfilPartes && _perfilPartes !== perfilPartes) setPerfilPartes(_perfilPartes);
          if (_rolPartes && _rolPartes !== rolPartes) setRolPartes(_rolPartes);
        }
      } catch (e) {
        console.warn("⚠️ No se pudo refrescar usuarios_partes:", e);
      }
    }

    const v = valida;
    if (!v.ok) {
      alert(v.msg);
      return;
    }

    try {
      setSaving(true);

      const realizaParte =
        bomberosCompania.find((b) => b.id === realizaParteId) ||
        bomberosTodos.find((b) => b.id === realizaParteId);

      const obacCuerpoSeleccionado =
        bomberosTodos.find((b) => String(b.id) === String(obacCuerpoId)) ||
        bomberosCompania.find((b) => String(b.id) === String(obacCuerpoId)) ||
        null;

      const asistentesFuente = compania ? bomberosCompania : bomberosTodos;

      const asistentesSnapshot = asistentesFuente
        .filter((b) => asistentesIds.includes(b.id))
        .map((b) => ({
          id: b.id,
          rut: b.rut || b.id,
          nombre: b.nombre || "",
          cargo: b.cargo || "",
          compania: b.compania || "",
        }));


      const unidadesParteSnapshot = unidadesParte
        .map((row) => {
          const uDoc = unidades.find((u) => u.id === row.unidadId);

          const obacCia =
            bomberosCompania.find((b) => b.id === row.obacCompaniaId) ||
            bomberosTodos.find((b) => b.id === row.obacCompaniaId);

          const conductor =
            bomberosCompania.find((b) => b.id === row.conductorId) ||
            bomberosTodos.find((b) => b.id === row.conductorId);

          const tieneAlgo =
            row.unidadId ||
            row.horaSalida ||
            row.horaLlegada ||
            row.kmSalida ||
            row.kmLlegada ||
            row.obacCompaniaId ||
            row.conductorId;

          if (!tieneAlgo) return null;

          return {
            unidadId: row.unidadId,
            unidadCodigo: uDoc?.codigo || "",
            horaSalida: row.horaSalida || "",
            horaLlegada: row.horaLlegada || "",
            kmSalida: row.kmSalida || "",
            kmLlegada: row.kmLlegada || "",
            obacCompania: obacCia
              ? { id: obacCia.id, rut: obacCia.rut || obacCia.id, nombre: obacCia.nombre || "" }
              : null,
            conductor: conductor
              ? { id: conductor.id, rut: conductor.rut || conductor.id, nombre: conductor.nombre || "" }
              : null,
          };
        })
        .filter(Boolean);

      const payload = {
        compania: (compania || _companiaNombre || companiaNombrePerfil || ""),
        // ✅ ID estable para permisos y búsquedas (evita diferencias por tildes/formatos)
        compania_id: _companiaDocId || companiaDocId || null,
        companiaDocId: _companiaDocId || companiaDocId || null,
        cuerpo_id: _cuerpoId || cuerpoId || null,
        fechaEmergencia: fechaEmergencia || null,
        horaDespacho: horaDespacho || null,

        tipoEmergenciaId,
        tipoEmergenciaNombre: tipoSeleccionado?.nombre || "",

        subTipoEmergenciaId: subTipoEmergenciaId || null,
        subTipoEmergenciaNombre: subTipoSeleccionado?.nombre || subTipoSeleccionado?.id || "",

        // ✅ detalle por tipo PRINCIPAL (ID del doc)
        detalleEspecifico: detalleAplica
          ? { tipo: tipoEmergenciaId, data: detalleEspecifico || null }
          : null,

        direccion: direccion.trim(),
        observaciones: observaciones.trim(),
        jurisdiccion,

        zona: zona || null,

        operadora: operadoraId
          ? (() => {
              const op = operadoras.find((x) => x.id === operadoraId) || null;
              if (!op) return { id: operadoraId };
              return {
                id: op.id,
                rut: op.rut || op.id,
                nombre: op.nombreCompleto || op.nombre || "",
              };
            })()
          : null,

        realizaParte: realizaParte
          ? {
              id: realizaParte.id,
              rut: realizaParte.rut || realizaParte.id,
              nombre: realizaParte.nombre || "",
              cargo: realizaParte.cargo || "",
              compania: realizaParte.compania || "",
            }
          : null,

        obacCuerpo: obacCuerpoSeleccionado
          ? {
              id: obacCuerpoSeleccionado.id,
              rut: obacCuerpoSeleccionado.rut || obacCuerpoSeleccionado.id,
              nombre: obacCuerpoSeleccionado.nombre || "",
              cargo: obacCuerpoSeleccionado.cargo || "",
              compania: obacCuerpoSeleccionado.compania || "",
            }
          : null,
        obacCuerpoId: obacCuerpoSeleccionado?.id || "",

        unidadesParte: unidadesParteSnapshot,
        asistentesIds,
        asistentesSnapshot,
        asistentesCount: asistentesIds.length,
        companias_despachadas_ids: companiasDespachadasIds,
        companias_despachadas_nombres: companiasDespachadasNombres,
        createdAt: serverTimestamp(),
      };


      // ✅ Guardar
      if (isEdit) {
        // En edición NO se incrementa folio, solo se actualiza el documento existente
        const user = auth.currentUser;
        const uid = user?.uid || "";
        const email = user?.email || "";

        const ref = doc(db, "partes_emergencia", parteId);

        // Detecta perfil compañía directamente desde usuarios_partes (no depende de estados que pueden no estar cargados)
        const perfilEff = String(_perfilPartes || perfilPartes || "").toLowerCase();
        const rolEff = String(_rolPartes || rolPartes || "").toLowerCase();
        // Para rules: cualquier usuario con perfil "compania" (ayudante de compañía) NO debe tocar asistencia global
        const esAyudanteCompania = perfilEff === "compania" && rolEff.includes("ayudante");

        await runTransaction(db, async (tx) => {
          // Trae doc actual para congelar campos estructurales y (si aplica) asistencia global
          const snap = await tx.get(ref);
          const actual = snap.exists() ? snap.data() : {};

          const payloadUpdate = {
            ...payload,

            // ✅ Congela SIEMPRE campos estructurales al editar
            compania: actual?.compania ?? payload.compania ?? "",
            compania_id: actual?.compania_id ?? payload.compania_id ?? null,
            companiaDocId: actual?.companiaDocId ?? payload.companiaDocId ?? null,
            cuerpo_id: actual?.cuerpo_id ?? payload.cuerpo_id ?? "",

            // ✅ Metadata update
            updatedAt: serverTimestamp(),
            updatedBy: { uid, email },
          };

          // ❌ en update no tocamos createdAt
          delete payloadUpdate.createdAt;

          // ✅ En edición NO debemos re-escribir campos estructurales de identificación
          // (evita permission-denied y evita corromper datos históricos)
          delete payloadUpdate.compania;
          delete payloadUpdate.compania_id;
          delete payloadUpdate.companiaDocId;
          delete payloadUpdate.cuerpo_id;

          // ✅ Si es Ayudante de Compañía: NO permitir cambios a asistencia global
          if (esAyudanteCompania) {
            // Para cumplir rules: NO enviar estos campos en el updateMask
            // (si van en el update, Firestore compara estructuras y puede denegar)
            delete payloadUpdate.asistentesIds;
            delete payloadUpdate.asistentesSnapshot;
            delete payloadUpdate.asistentesCount;
          }

          tx.update(ref, payloadUpdate);
        });

        alert(`✅ Parte actualizado.
ID: ${parteId}`);
        navigate(partesBase);
      } else {
        alert(`✅ Parte actualizado.
ID: ${parteId}`);
        navigate(partesBase);
        // ✅ Guardado ATÓMICO: incrementa folio + crea el parte + borra "en creación" (si existe)
        const res = await obtenerFolioYCrearParte(payload, { enCreacionDocId });
        const folio = res?.folio || res?.correlativo || "";
        const newParteId = res?.parteId || "";

        // limpiar marca local (ya se borró en la transacción)
        setEnCreacionDocId("");
        setCorrelativoNumReservado(null);
        setCorrelativoYearReservado(null);
        setCorrelativoPreview("");

        alert(`✅ Parte guardado.
Compañía: ${compania}
Folio: ${folio}
ID: ${newParteId}`);
        navigate(partesBase);
      }
    } catch (e) {
      console.error("❌ Error guardando el parte:", e);

      const code = e?.code || "";
      const msg = e?.message || String(e);
      const name = e?.name || "";

      // Si falta índice, Firestore suele traer un link en el mensaje
      const indexUrlMatch = typeof msg === "string"
        ? msg.match(/https:\/\/console\.firebase\.google\.com\/[^\s]+/)
        : null;
      const indexUrl = indexUrlMatch ? indexUrlMatch[0] : "";

      let extra = "";
      if (code === "permission-denied") {
        extra = "PERMISO DENEGADO (rules).";
      } else if (code === "failed-precondition") {
        extra = "Firestore pide un ÍNDICE (failed-precondition).";
      } else if (code === "invalid-argument") {
        extra = "Dato inválido enviado a Firestore (invalid-argument).";
      }

      alert(
        `❌ Error guardando el parte\n\n` +
        `code: ${code}\n` +
        `name: ${name}\n\n` +
        `${msg}` +
        (indexUrl ? `\n\n🔗 Crear índice:\n${indexUrl}` : "") +
        (extra ? `\n\n👉 ${extra}` : "")
      );
    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return <div style={{ padding: 16 }}>Cargando formulario...</div>;
  }

  const grid2Cols = isSmall ? "1fr" : "repeat(2, minmax(0, 1fr))";
  const grid3Cols = isSmall ? "1fr" : "repeat(3, minmax(0, 1fr))";
  const asistenciaCols = isSmall ? "1fr" : "repeat(2, minmax(0, 1fr))";

  const esAyudanteCompania = perfilPartes === "compania" && rolPartes === "Ayudante de Compañía";
  const companiaDelUsuario = companiaNombrePerfil || "";

  return (
    <div style={S.page} className="pf-page">
      <style>{`
        .pf-page { width: 100%; }
        .pf-card { width: 100%; box-sizing: border-box; }
        @media (max-width: 640px) {
          .pf-card { padding: 16px !important; border-radius: 18px !important; }
          .pf-title { font-size: 34px !important; line-height: 1.05 !important; }
        }
      `}</style>

      <div style={S.container}>
        <div style={S.topbar}>
          <button style={S.backBtn} onClick={() => navigate(partesBase)}>
            ← Volver
          </button>
          <div style={S.head}>
            <div style={S.title} className="pf-title">Parte de Emergencia</div>
            <div style={S.subtitle}>{compania ? compania : "Todas las compañías"}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }} className="pf-version">Formulario v{FORM_VERSION}</div>
          </div>
        </div>

        {compania && bomberosCompania.length === 0 && (
          <div style={{ ...S.card, border: "1px solid #fecaca", background: "#fff1f2" }}>
            <b>⚠️ No hay bomberos registrados para esta compañía:</b> {compania}
            <div style={{ marginTop: 6, color: "#991b1b" }}>
              Revisa que en la colección <b>bomberos</b> el campo <b>compania</b> coincida
              exactamente con el nombre seleccionado.
            </div>
          </div>
        )}

        <div style={S.card} className="pf-card">
          <div style={{ ...S.grid, gridTemplateColumns: grid2Cols }}>
            <div>
              <label style={S.label}>Correlativo (se genera automáticamente)</label>
                  <input
                    value={correlativoPreview || "— se asignará al guardar —"}
                    disabled
                    style={{ ...S.input, background: "#f3f4f6" }}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginTop: 10,
                    }}
                  >
                    <div>
                      <label style={S.label}>Fecha emergencia</label>
                      <input
                        type="date"
                        value={fechaEmergencia}
                        onChange={(e) => setFechaEmergencia(e.target.value)}
                        style={S.input}
                      />
                    </div>

                    <div>
                      <label style={S.label}>Hora despacho</label>
                      <Time24Select
                        value={horaDespacho}
                        onChange={(val) => setHoraDespacho(val)}
                        disabled={saving}
                      />
                    </div>
                  </div>
            </div>

            <div>
              <label style={S.label}>Tipo de emergencia</label>
              <select
                value={tipoEmergenciaId}
                onChange={(e) => setTipoEmergenciaId(e.target.value)}
                style={S.input}
              >
                <option value="">— Seleccionar —</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>
              {tipos.length === 0 && (
                <div style={S.hint}>
                  No hay tipos cargados en <b>tiposEmergencia/items/items</b>.
                </div>
              )}
            </div>

            {/* ✅ Subclave dependiente */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>Seleccione la Subclave</label>
              <select
                value={subTipoEmergenciaId}
                onChange={(e) => setSubTipoEmergenciaId(e.target.value)}
                style={S.input}
                disabled={subTipos.length === 0}
              >
                <option value="">
                  {subTipos.length === 0 ? "— No aplica —" : "— Seleccionar —"}
                </option>
                {subTipos.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.nombre || it.id}
                  </option>
                ))}
              </select>
              
            </div>

            
            {/* ✅ NUEVO: Compañías despachadas (chips tipo ticket) */}
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={S.subCard}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>Compañías despachadas</div>
                <div style={S.chipsWrap}>
                  {Object.entries(companiasById || {})
                    .map(([id, nombre]) => ({ id, nombre }))
                    .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)))
                    .map((c) => {
                      const on = companiasDespachadasIds.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleCompaniaDespachada(c)}
                          style={{ ...S.chip, ...(on ? S.chipOn : {}) }}
                        >
                          {c.nombre}
                        </button>
                      );
                    })}
                </div>

                <div style={S.hint}>
                  Selecciona las compañías que fueron despachadas. Esto se usa para informes y gráficos.
                </div>
              </div>
            </div>


            {/* ✅ NUEVO: Formulario dependiente por CLAVE PRINCIPAL (ID real) */}
            {detalleAplica && (
              <div style={{ gridColumn: "1 / -1" }}>

                {/* ESTRUCTURAL */}
                {tipoEmergenciaId === DETALLE_TIPO_IDS.ESTRUCTURAL && (
                  <div style={S.subCard}>
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>
                      Detalle del Llamado Estructural
                    </div>

                    {(detalleEspecifico?.edificaciones || []).map((ed, idx) => (
                      <div key={idx} style={S.blockBox}>
                        <div style={S.blockHeader}>
                          <div style={{ fontWeight: 900 }}>Edificación / Dpto {idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => removeEdificacion(idx)}
                            style={S.smallDanger}
                            disabled={(detalleEspecifico?.edificaciones || []).length === 1}
                          >
                            Eliminar
                          </button>
                        </div>

                        <div style={{ ...S.grid, gridTemplateColumns: grid2Cols }}>
                          <div>
                            <label style={S.label}>Tipo de edificación</label>
                            <input
                              value={ed.tipoEdificacion || ""}
                              onChange={(e) =>
                                setDetalleEdifField(idx, "tipoEdificacion", e.target.value)
                              }
                              placeholder="Ej: Casa, Dpto, Galpón..."
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>N° casa o dpto</label>
                            <input
                              value={ed.numero || ""}
                              onChange={(e) => setDetalleEdifField(idx, "numero", e.target.value)}
                              placeholder="Ej: 12 / 504"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Cuántos pisos</label>
                            <input
                              value={ed.pisos || ""}
                              onChange={(e) => setDetalleEdifField(idx, "pisos", e.target.value)}
                              placeholder="Ej: 1, 2, 3..."
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Estructura afectada</label>
                            <input
                              value={ed.estructuraAfectada || ""}
                              onChange={(e) =>
                                setDetalleEdifField(idx, "estructuraAfectada", e.target.value)
                              }
                              placeholder="Ej: Techo, cocina, 2° piso..."
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Nombre propietario</label>
                            <input
                              value={ed.propietarioNombre || ""}
                              onChange={(e) =>
                                setDetalleEdifField(idx, "propietarioNombre", e.target.value)
                              }
                              placeholder="Nombre completo"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>RUT propietario</label>
                            <input
                              value={ed.propietarioRut || ""}
                              onChange={(e) =>
                                setDetalleEdifField(idx, "propietarioRut", formatearRut(e.target.value))
                              }
                              placeholder="12345678-9"
                              style={S.input}
                            />
                          </div>

                          <div style={{ gridColumn: "1 / -1" }}>
                            <label style={S.label}>Teléfono propietario</label>
                            <input
                              value={ed.propietarioTelefono || ""}
                              onChange={(e) =>
                                setDetalleEdifField(idx, "propietarioTelefono", e.target.value)
                              }
                              placeholder="+56 9 ..."
                              style={S.input}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button type="button" onClick={addEdificacion} style={S.smallBtn}>
                      + Agregar otra edificación/departamento
                    </button>
                  </div>
                )}

                {/* PASTIZALES */}
                {tipoEmergenciaId === DETALLE_TIPO_IDS.PASTIZALES && (
                  <div style={S.subCard}>
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>
                      Detalles del Llamado de pastizales o basura.
                    </div>

                    <div style={{ ...S.grid, gridTemplateColumns: grid2Cols }}>
                      <div>
                        <label style={S.label}>Metros cuadrados afectados</label>
                        <input
                          value={detalleEspecifico?.metrosCuadradosAfectados || ""}
                          onChange={(e) => setDetalleField("metrosCuadradosAfectados", e.target.value)}
                          placeholder="Ej: 300"
                          style={S.input}
                        />
                      </div>

                      <div>
                        <label style={S.label}>¿Árboles nativos?</label>
                        <select
                          value={detalleEspecifico?.arbolesNativos || ""}
                          onChange={(e) => setDetalleField("arbolesNativos", e.target.value)}
                          style={S.input}
                        >
                          <option value="">— Seleccionar —</option>
                          <option value="SI">Sí</option>
                          <option value="NO">No</option>
                        </select>
                      </div>

                      <div>
                        <label style={S.label}>Nombre propietario sitio</label>
                        <input
                          value={detalleEspecifico?.propietarioNombre || ""}
                          onChange={(e) => setDetalleField("propietarioNombre", e.target.value)}
                          placeholder="Nombre completo"
                          style={S.input}
                        />
                      </div>

                      <div>
                        <label style={S.label}>RUT propietario sitio</label>
                        <input
                          value={detalleEspecifico?.propietarioRut || ""}
                          onChange={(e) => setDetalleField("propietarioRut", formatearRut(e.target.value))}
                          placeholder="12345678-9"
                          style={S.input}
                        />
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={S.label}>Teléfono propietario sitio</label>
                        <input
                          value={detalleEspecifico?.propietarioTelefono || ""}
                          onChange={(e) => setDetalleField("propietarioTelefono", e.target.value)}
                          placeholder="+56 9 ..."
                          style={S.input}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* RESCATE VEHICULAR */}
                {tipoEmergenciaId === DETALLE_TIPO_IDS.RESCATE_VEHICULAR && (
                  <div style={S.subCard}>
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>
                      Detalles del rescate vehícular
                    </div>

                    <div style={{ marginBottom: 10 }}>
                      <label style={S.label}>¿Se realiza extricación?</label>
                      <select
                        value={detalleEspecifico?.extricacion || ""}
                        onChange={(e) => setDetalleField("extricacion", e.target.value)}
                        style={S.input}
                      >
                        <option value="">— Seleccionar —</option>
                        <option value="SI">Sí</option>
                        <option value="NO">No</option>
                      </select>
                    </div>

                    {(detalleEspecifico?.vehiculos || []).map((vh, idx) => (
                      <div key={idx} style={S.blockBox}>
                        <div style={S.blockHeader}>
                          <div style={{ fontWeight: 900 }}>Vehículo {idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => removeVehiculo(idx)}
                            style={S.smallDanger}
                            disabled={(detalleEspecifico?.vehiculos || []).length === 1}
                          >
                            Eliminar
                          </button>
                        </div>

                        <div style={{ ...S.grid, gridTemplateColumns: grid2Cols }}>
                          <div>
                            <label style={S.label}>Tipo de vehículo</label>
                            <input
                              value={vh.tipoVehiculo || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "tipoVehiculo", e.target.value)
                              }
                              placeholder="Ej: Sedán, Camioneta, Camión..."
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Marca</label>
                            <input
                              value={vh.marca || ""}
                              onChange={(e) => setDetalleVehField(idx, "marca", e.target.value)}
                              placeholder="Ej: Toyota"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Modelo</label>
                            <input
                              value={vh.modelo || ""}
                              onChange={(e) => setDetalleVehField(idx, "modelo", e.target.value)}
                              placeholder="Ej: Hilux"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Patente</label>
                            <input
                              value={vh.patente || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "patente", String(e.target.value || "").toUpperCase())
                              }
                              placeholder="Ej: ABCL12"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Tipo de combustible</label>
                            <input
                              value={vh.tipoCombustible || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "tipoCombustible", e.target.value)
                              }
                              placeholder="Ej: Bencina / Diésel / Eléctrico"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>Nombre propietario</label>
                            <input
                              value={vh.propietarioNombre || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "propietarioNombre", e.target.value)
                              }
                              placeholder="Nombre completo"
                              style={S.input}
                            />
                          </div>

                          <div>
                            <label style={S.label}>RUT propietario</label>
                            <input
                              value={vh.propietarioRut || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "propietarioRut", formatearRut(e.target.value))
                              }
                              placeholder="12345678-9"
                              style={S.input}
                            />
                          </div>

                          <div style={{ gridColumn: "1 / -1" }}>
                            <label style={S.label}>Teléfono propietario</label>
                            <input
                              value={vh.propietarioTelefono || ""}
                              onChange={(e) =>
                                setDetalleVehField(idx, "propietarioTelefono", e.target.value)
                              }
                              placeholder="+56 9 ..."
                              style={S.input}
                            />                          </div>
                        </div>
                      </div>
                    ))}

                    <button type="button" onClick={addVehiculo} style={S.smallBtn}>
                      + Agregar otro vehículo
                    </button>
                  </div>
                )}

                
              </div>
            )}

            <div>
              <label style={S.label}>Dirección</label>
              <input
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
                placeholder="Ej: Av. Principal 123"
                style={S.input}
              />
            </div>

            <div>
              <label style={S.label}>Jurisdicción</label>
              <select
                value={jurisdiccion}
                onChange={(e) => setJurisdiccion(e.target.value)}
                style={S.input}
              >
                <option value="">— Seleccionar —</option>
                {jurisdicciones.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
              {jurisdicciones.length === 0 && (
                <div style={S.hint}>
                  No hay jurisdicciones (colección <b>jurisdicciones</b>).
                </div>
              )}
            </div>

            {/* Zona (urbano/rural/costa) */}
            <div>
              <label style={S.label}>Seleccionar Zona</label>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6 }}>
                {["urbano", "rural", "costa"].map((z) => (
                  <label key={z} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="radio"
                      name="zona"
                      value={z}
                      checked={zona === z}
                      onChange={(e) => setZona(e.target.value)}
                    />
                    <span style={{ fontWeight: 700 }}>{z.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Operadora (despacho) */}
            <div>
              <label style={S.label}>Operadora que realiza el despacho</label>
              <select
                value={operadoraId}
                onChange={(e) => setOperadoraId(e.target.value)}
                style={S.input}
              >
                <option value="">— Seleccionar —</option>
                {operadoras.map((op) => (
                  <option key={op.id} value={op.id}>
                    {(op.nombreCompleto || op.nombre || "Sin nombre")} ({op.rut || op.id})
                  </option>
                ))}
              </select>
              {operadoras.length === 0 && (
                <div style={S.hint}>
                  No hay operadoras (colección <b>operadoras</b>).
                </div>
              )}
            </div>

            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>Observaciones</label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Opcional"
                style={{ ...S.input, minHeight: 90, resize: "vertical" }}
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <BomberoSearchSelect
                label="OBAC Cuerpo"
                valueId={obacCuerpoId}
                onChange={setObacCuerpoId}
                query={obacCuerpoQuery}
                setQuery={setObacCuerpoQuery}
                bomberos={bomberosTodos}
                disabled={saving}
                styles={{
                  wrap: {},
                  label: S.label,
                  input: S.input,
                  hint: S.hint,
                  smallBtn: { ...S.smallBtn, width: "auto", padding: "10px 14px" },
                  dropdown: S.searchDropdown,
                  dropdownItem: S.searchDropdownItem,
                }}
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <BomberoSearchSelect
                label="Quién realiza el parte"
                valueId={realizaParteId}
                onChange={setRealizaParteId}
                query={realizaParteQuery}
                setQuery={setRealizaParteQuery}
                bomberos={bomberosTodos}
                disabled={saving}
                styles={{
                  wrap: {},
                  label: S.label,
                  input: S.input,
                  hint: S.hint,
                  smallBtn: { ...S.smallBtn, width: "auto", padding: "10px 14px" },
                  dropdown: S.searchDropdown,
                  dropdownItem: S.searchDropdownItem,
                }}
              />
            </div>
          </div>

        {/* Unidades */}
        <div style={S.card} className="pf-card">
          <div style={S.sectionTitle}>
            Unidades (opcional)
          </div>

          {unidadesParte.map((row, idx) => (
            <div key={idx} style={S.unitRow}>
              <div style={S.unitRowHeader}>
                <div style={{ fontWeight: 900 }}>Unidad #{idx + 1}</div>
                <button
                  onClick={() => removeUnidadRow(idx)}
                  style={S.smallDanger}
                  disabled={unidadesParte.length === 1}
                >
                  Eliminar
                </button>
              </div>

              <div style={{ ...S.grid, gridTemplateColumns: grid3Cols }}>
                <div>
                  <label style={S.label}>Unidad</label>
                  <select
                    value={row.unidadId}
                    onChange={(e) => updateUnidadRow(idx, { unidadId: e.target.value })}
                    style={S.input}
                  >
                    <option value="">— Seleccionar —</option>
                    {unidades.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.codigo}
                      </option>
                    ))}
                  </select>
                  {unidades.length === 0 && (
                    <div style={S.hint}>
                      No hay unidades (colección <b>unidades</b>).
                    </div>
                  )}
                </div>

                <div>
                  <label style={S.label}>Hora salida (24h)</label>
                  <Time24Select
                    value={row.horaSalida}
                    onChange={(val) => updateUnidadRow(idx, { horaSalida: val })}
                    disabled={saving}
                  />
                </div>

                <div>
                  <label style={S.label}>Hora llegada (24h)</label>
                  <Time24Select
                    value={row.horaLlegada}
                    onChange={(val) => updateUnidadRow(idx, { horaLlegada: val })}
                    disabled={saving}
                  />
                </div>

                <div>
                  <label style={S.label}>KM salida</label>
                  <input
                    value={row.kmSalida}
                    onChange={(e) => updateUnidadRow(idx, { kmSalida: e.target.value })}
                    style={S.input}
                    placeholder="Ej: 12540"
                  />
                </div>

                <div>
                  <label style={S.label}>KM llegada</label>
                  <input
                    value={row.kmLlegada}
                    onChange={(e) => updateUnidadRow(idx, { kmLlegada: e.target.value })}
                    style={S.input}
                    placeholder="Ej: 12580"
                  />
                </div>

                <div>
                  <label style={S.label}>OBAC Compañía</label>
                  <select
                    value={row.obacCompaniaId}
                    onChange={(e) =>
                      updateUnidadRow(idx, { obacCompaniaId: e.target.value })
                    }
                    style={S.input}
                  >
                    <option value="">— Seleccionar —</option>
                    {row.unidadId ? (
                      obacCompaniaOptionsByRow(idx).map((b) => (
                        <option key={b.id} value={b.id}>
                          {bomberoLabel(b)}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        Primero selecciona una unidad
                      </option>
                    )}</select>
                </div>

                <div>
                  <label style={S.label}>Conductor</label>
                  <select
                    value={row.conductorId}
                    onChange={(e) => updateUnidadRow(idx, { conductorId: e.target.value })}
                    style={S.input}
                  >
                    <option value="">— Seleccionar —</option>
                    {maquinistasTodos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {bomberoLabel(b)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}

          <button onClick={addUnidadRow} style={S.smallBtn}>
            + Agregar unidad
          </button>
        </div>

        {/* Asistencia */}
        <div style={S.card} className="pf-card">
          <div style={S.sectionTitle}>Asistencia (selección múltiple)</div>
          <div style={S.hint}>Marca los bomberos presentes {compania ? `de ${compania}` : "de todas las compañías"}.</div>

          <div style={{ ...S.asistenciaBox, gridTemplateColumns: asistenciaCols }}>
            {compania ? (
              bomberosCompania.map((b) => {
                const checked = asistentesIds.includes(b.id);
                return (
                  <label key={b.id} style={S.asistenteItem}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={esAyudanteCompania && compania && companiaDelUsuario && companiaDelUsuario !== compania}
                      onChange={() => {
                        if (esAyudanteCompania && compania && companiaDelUsuario && companiaDelUsuario !== compania) return;
                        toggleAsistente(b.id);
                      }}
                    />
                    <span style={{ marginLeft: 8 }}>
                      {bomberoLabel(b)}
                    </span>
                  </label>
                );
              })
            ) : (
              Object.entries(bomberosPorCompania || {}).map(([cia, list], idx) => (
                <React.Fragment key={cia}>
                  {idx > 0 && (
                    <div style={S.asistenciaDivider} />
                  )}
                  <div style={{ gridColumn: "1 / -1", fontWeight: 900, marginTop: 8 }}>
                    {cia}
                  </div>
                  {list.map((b) => {
                    const checked = asistentesIds.includes(b.id);
                    return (
                      <label key={b.id} style={S.asistenteItem}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={esAyudanteCompania && companiaDelUsuario && b?.compania !== companiaDelUsuario}
                          onChange={() => {
                            if (esAyudanteCompania && companiaDelUsuario && b?.compania !== companiaDelUsuario) return;
                            toggleAsistente(b.id);
                          }}
                        />
                        <span style={{ marginLeft: 8 }}>
                          {bomberoLabel(b)}
                        </span>
                      </label>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </div>

          <div style={{ marginTop: 10, fontWeight: 800 }}>
            Total seleccionados: {asistentesIds.length}
          </div>
        </div>

        {/* Guardar */}
        <div style={S.footer}>
          <button
            onClick={guardar}
            disabled={saving}
            style={{
              ...S.saveBtn,
              opacity: saving ? 0.6 : 1,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Guardando..." : "Guardar parte"}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: 14, background: "#0b1220", minHeight: "100vh" },
  container: { width: "min(1100px, 96vw)", margin: "0 auto" },

  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    color: "white",
    flexWrap: "wrap",
  },
  backBtn: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.2)",
    background: "rgba(255,255,255,.08)",
    color: "white",
    cursor: "pointer",
  },
  head: { display: "flex", flexDirection: "column" },
  title: { fontSize: 18, fontWeight: 900 },
  subtitle: { opacity: 0.8 },

  card: {
    background: "white",
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
    marginBottom: 12,
  },
  sectionTitle: { fontWeight: 900, marginBottom: 10, fontSize: 16 },

  sectionTitleMini: { fontWeight: 900, marginBottom: 10, fontSize: 14 },

  subCard: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },

  blockBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    background: "#fafafa",
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 10,
  },

  grid: { display: "grid", gap: 12 },
  label: { display: "block", marginBottom: 6, fontWeight: 700 },

  input: {
    width: "100%",
    padding: 10,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    outline: "none",
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: 0,
  },

  hint: { marginTop: 6, color: "#6b7280", fontSize: 13 },

  searchDropdown: {
    marginTop: 8,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    background: "#ffffff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    maxHeight: 260,
    overflowY: "auto",
  },
  searchDropdownItem: {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    border: "none",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },

  unitRow: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    background: "#fafafa",
  },
  unitRowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    gap: 8,
    flexWrap: "wrap",
  },
  smallBtn: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    fontWeight: 800,
  },
  smallDanger: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #fecaca",
    background: "#fff1f2",
    cursor: "pointer",
    fontWeight: 800,
    color: "#991b1b",
  },

  asistenciaBox: { marginTop: 10, display: "grid", gap: 8 },
  asistenciaDivider: {
    height: 1,
    background: "#e5e7eb",
    margin: "18px 0 10px",
    gridColumn: "1 / -1",
  },
  asistenteItem: {
    display: "flex",
    alignItems: "center",
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "white",
  },

  // ✅ Chips (tickets) para Compañías despachadas
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    lineHeight: 1,
  },
  chipOn: {
    background: "#16a34a",
    border: "1px solid #15803d",
    color: "white",
  },


  footer: { paddingBottom: 30 },
  saveBtn: {
    width: "100%",
    padding: 14,
    borderRadius: 12,
    border: "none",
    background: "#16a34a",
    color: "white",
    fontWeight: 900,
    fontSize: 16,
  },

  timeWrap: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 8,
    alignItems: "center",
  },
  timeSelect: {
    width: "100%",
    padding: 10,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
    background: "white",
  },
  timeColon: {
    fontWeight: 900,
    opacity: 0.7,
    marginTop: -1,
  },
};